import { auth } from '@/services/firebase';
import APP_CONFIG from '../app.config';
import { submitSupportTicket } from '../services/supportTicketService';

const ALLOWED_TYPES = ['bug', 'support', 'suggestion'];
const ALLOWED_SEVERITIES = ['low', 'medium', 'high', 'critical'];

const normalizeText = (value, maxLength = 500) => {
  const normalized = String(value ?? '').trim();
  return normalized ? normalized.slice(0, maxLength) : undefined;
};

const getClientEnvironment = () => {
  const mode = String(import.meta.env.MODE || '').toLowerCase();
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';

  if (mode === 'development' || host.includes('localhost') || host.includes('127.0.0.1')) return 'dev';
  if (host.includes('staging') || host.includes('-tst') || host.includes('test')) return 'tst';
  if (mode === 'production') return 'prod';
  return 'unknown';
};

const getSafePagePath = () => {
  if (typeof window === 'undefined') return '/';
  const hash = String(window.location.hash || '').slice(0, 80);
  return `${window.location.pathname || '/'}${hash}`.slice(0, 180);
};

const getBrowserSummary = () => {
  if (typeof navigator === 'undefined') return 'Inconnu';
  const ua = navigator.userAgent || '';
  let browser = 'Navigateur inconnu';

  if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari')) browser = 'Safari';

  const platform = navigator.userAgentData?.platform || navigator.platform || 'plateforme inconnue';
  return `${browser} sur ${platform}`.slice(0, 120);
};

const normalizeSupportError = (error) => {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').trim();

  if (code.includes('unauthenticated')) {
    return 'Veuillez vous connecter pour envoyer un signalement.';
  }

  if (code.includes('failed-precondition')) {
    return message || 'Un signalement a déjà été envoyé récemment. Merci de patienter un instant.';
  }

  if (code.includes('invalid-argument')) {
    return message || 'Certains champs du formulaire sont invalides.';
  }

  if (code.includes('unavailable') || /network|fetch|offline/i.test(message)) {
    return 'Connexion impossible pour le moment. Vérifiez votre réseau puis réessayez.';
  }

  if (!message || /submitsupportticket|https|internal/i.test(message)) {
    return 'Impossible d’envoyer le signalement pour le moment. Réessayez dans quelques instants.';
  }

  return message;
};

const buildSupportPayload = (payload, options, user) => {
  const type = ALLOWED_TYPES.includes(payload?.type) ? payload.type : 'support';
  const title = normalizeText(payload?.title, 120);
  const description = normalizeText(payload?.description, 4000);

  if (!title || !description) {
    throw new Error('Merci de renseigner un titre et une description.');
  }

  const allowTechnicalContext = payload?.allowTechnicalContext !== false;
  const stepsToReproduce = normalizeText(payload?.stepsToReproduce, 3000);
  const expectedResult = normalizeText(payload?.expectedResult, 1500);
  const actualResult = normalizeText(payload?.actualResult, 1500);
  const moduleName = normalizeText(payload?.module || options?.moduleHint, 80);
  const contactEmail = normalizeText(payload?.contactEmail || user?.email, 254);
  const severity = ALLOWED_SEVERITIES.includes(payload?.severity) ? payload.severity : 'medium';

  const cleanedPayload = {
    type,
    title,
    description,
    ...(type === 'bug' ? { severity } : {}),
    ...(stepsToReproduce ? { stepsToReproduce } : {}),
    ...(expectedResult ? { expectedResult } : {}),
    ...(actualResult ? { actualResult } : {}),
    ...(moduleName ? { module: moduleName } : {}),
    ...(contactEmail ? { contactEmail } : {}),
    allowTechnicalContext,
  };

  if (allowTechnicalContext) {
    const metadata = {
      page: getSafePagePath(),
      appVersion: normalizeText(APP_CONFIG.appVersion || 'inconnue', 40),
      environment: getClientEnvironment(),
      browser: getBrowserSummary(),
    };

    const filteredMetadata = Object.fromEntries(
      Object.entries(metadata).filter(([, value]) => Boolean(value))
    );

    if (Object.keys(filteredMetadata).length > 0) {
      cleanedPayload.metadata = filteredMetadata;
    }
  }

  return cleanedPayload;
};

const useContactDeveloper = () => {
  const handleContactSubmit = async (payload, options = {}) => {
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Veuillez vous connecter pour envoyer un signalement.');
    }

    try {
      return await submitSupportTicket(buildSupportPayload(payload, options, user));
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Erreur envoi ticket support :', err?.message || err);
      }
      throw new Error(normalizeSupportError(err));
    }
  };

  return handleContactSubmit;
};

export default useContactDeveloper;
